import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Croppie from 'croppie'

import { colors } from 'helpers/colors'
import Text from 'components/reusable/text'
import Button from 'components/reusable/button'
import Flex from 'components/reusable/flex'
import Indent from 'components/reusable/indent'
import SvgCrossMarkSolid from 'components/reusable/svg/svg_cross_mark_solid'

const CROPPIE_PREVIEW_ID = 'image_crop_modal_preview'

const ImageCropModal = ({ modalProps = {}, onClose }) => {
  const { imageUrl, onConfirm } = modalProps
  const croppieRef = useRef(null)
  const croppieOptsRef = useRef({ enableExif: true, viewport: { width: 400, height: 400 }, boundary: { width: 500, height: 500 } })

  useEffect(() => {
    if (imageUrl) {
      const previewNode = document.getElementById(CROPPIE_PREVIEW_ID)
      if (previewNode && !croppieRef.current) {
        croppieRef.current = new Croppie(previewNode, croppieOptsRef.current)
        croppieRef.current.bind({ url: imageUrl })
      }
    }

    return () => {
      if (croppieRef.current) {
        croppieRef.current.destroy()
        croppieRef.current = null
      }
    }
  }, [imageUrl])

  const handleCancel = useCallback(() => {
    if (croppieRef.current) {
      croppieRef.current.destroy()
      croppieRef.current = null
    }
    onClose()
  }, [onClose])

  const handleConfirm = useCallback(async () => {
    if (!croppieRef.current || !onConfirm) return

    const crop = croppieRef.current.get()
    const points = crop.points.map((p) => parseInt(p, 10))
    const [topLeftX, topLeftY, bottomRightX, bottomRightY] = points
    const { width, height } = croppieOptsRef.current.boundary
    const box_width = bottomRightX - topLeftX
    const box_height = bottomRightY - topLeftY
    const db_box_width = width > 0 ? (box_width * 100) / width : 0
    const db_box_height = height > 0 ? (box_height * 100) / height : 0
    const db_offset_top = box_height > 0 ? (topLeftY * 100) / box_height : 0
    const db_offset_left = box_width > 0 ? (topLeftX * 100) / box_width : 0

    const cropData = {
      topLeftX,
      topLeftY,
      bottomRightX,
      bottomRightY,
      width,
      height,
      box_width,
      box_height,
      db_box_width,
      db_box_height,
      db_offset_top,
      db_offset_left,
    }

    const croppedBase64 = await croppieRef.current.result()
    const originalBase64 = croppieRef.current.data.url

    const imageData = { croppedBase64, originalBase64, croppedImg: croppedBase64, image: originalBase64, crop: cropData }

    onConfirm(imageData)
    handleCancel()
  }, [onConfirm, handleCancel])

  return (
    <Wrapper dir="column" wrap="nowrap">
      <CloseButton type="semantic-wrapper" onClick={handleCancel} aria-label="Close">
        <SvgCrossMarkSolid size={20} />
      </CloseButton>

      <ModalTitle>Crop Image</ModalTitle>
      <Indent bottom={20} />

      <CropperContainer>
        <div id={CROPPIE_PREVIEW_ID} />
      </CropperContainer>

      <Indent bottom={24} />

      <Flex dir="row" gap={8} width="100%">
        <StyledButton type="gray-shadow" height={40} onClick={handleCancel}>
          Cancel
        </StyledButton>
        <StyledButton type="primary" height={40} onClick={handleConfirm}>
          Confirm
        </StyledButton>
      </Flex>
    </Wrapper>
  )
}

const Wrapper = styled(Flex)`
  max-width: 600px;
  width: 100%;
  padding: 32px 40px 24px;
  position: relative;
  @media (max-width: 768px) {
    padding: 24px 16px 16px;
  }
`

const CloseButton = styled(Button)`
  position: absolute;
  top: 30px;
  right: 40px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
  }
`

const ModalTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin: 0;
`

const CropperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;

  #${CROPPIE_PREVIEW_ID} {
    margin: 0 !important;
  }
`

const StyledButton = styled(Button)`
  flex: 1;
`

export default ImageCropModal
